import { cn } from '@udecode/cn'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@wellesley/ui'
import useActionGuard from 'hooks/useActionGuard'
import { FC, PropsWithChildren } from 'react'
import { Action } from './types'

type MenuContentProps = {
  renderItemWrapper: (action: Action, el: JSX.Element) => JSX.Element
  actions: Action[]
  adminActions: Action[]
}

const MenuContent: FC<PropsWithChildren<MenuContentProps>> = ({
  children,
  actions,
  adminActions,
  renderItemWrapper,
}) => {
  const { ActionTooltipWrapper } = useActionGuard()

  return (
    <>
      {children}
      {actions.map((action) => {
        if (action.isHidden) return null

        return (
          <div key={action.name}>
            <ActionTooltipWrapper triggerClassname="w-full">
              {renderItemWrapper(
                action,
                <div
                  className={cn(
                    'flex min-h-[24px] w-full items-center text-start text-base',
                    {
                      'opacity-50': action.disabled,
                    },
                  )}
                >
                  {action.link ? (
                    <a
                      href={action.link}
                      target="_blank"
                      rel="noreferrer"
                      className="gap-xxs [&_svg]:size-xs flex items-center"
                    >
                      {action.icon}
                      {action.name}
                    </a>
                  ) : (
                    <div className="gap-xxs [&_svg]:size-xxs flex w-full items-center justify-between">
                      <div className="gap-xxs [&_svg]:size-xxs flex items-center">
                        {action.icon}
                        {action.name}
                      </div>
                      {action.rightElement && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center"
                        >
                          {action.rightElement}
                        </div>
                      )}
                    </div>
                  )}
                </div>,
              )}
            </ActionTooltipWrapper>

            {action.isSeparator && (
              <p
                key="menu_separator"
                className="my-xxs border-t border-dashed border-gray-200"
              />
            )}
          </div>
        )
      })}
      {!!adminActions.length && (
        <>
          <p
            key="menu_separator"
            className="my-xxs border-t border-dashed border-gray-200"
          />
          <DropdownMenu>
            <DropdownMenuTrigger className="p-xxs w-full text-left hover:bg-gray-100 focus-visible:outline-none">
              Moderate
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              className="p-xxs min-w-[200px] text-base font-semibold text-gray-700"
            >
              {adminActions.map((action) => {
                if (action.isHidden) return null

                return (
                  <div key={action.name}>
                    <DropdownMenuItem
                      onClick={action.handler}
                      className={cn(
                        'h-m cursor-pointer',
                        action.className,
                        action.isSelected && 'font-semibold',
                      )}
                    >
                      {action.link ? (
                        <a href={action.link} target="_blank" rel="noreferrer">
                          {action.name}
                        </a>
                      ) : (
                        action.name
                      )}
                    </DropdownMenuItem>
                    {action.isSeparator && (
                      <p
                        key="menu_separator"
                        className="my-xxxs border-t border-dashed border-gray-200"
                      />
                    )}
                  </div>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </>
  )
}

export default MenuContent
